import './polyfills';

import * as ReactDOM from 'react-dom/client';

import { App } from './App';
import type { User } from './domain/user';
import { userApi } from './features/user/api';
import userQueryKey from './features/user/userQueryKey';
import { initUseCases } from './initUseCases';
import { makeMessageHandler } from './messageHandler';
import { queryClient } from './queryClient';
import { appStore, AppMode, initialState } from './services/appStore';
import { onInvalidateUserQuery } from './services/broadcastChannel';
import { postChromeTabUpdateConfig } from './services/chromeTabUpdate/postChromeTabUpdateConfig';
import { initI18n } from './services/i18n';
import { postCss } from './services/iframe_css/postCss';
import { initSentry } from './services/sentry';
import { initGrowthBook } from './services/tracking/growthbook';
import { initTracking } from './services/tracking/tracking';
import { initMessaging, postBadgeVisibilityConfig, postConfig } from './services/windowMessaging';

initI18n(window.i18nURL).catch(console.error);

const gb = initGrowthBook();
initTracking();
initSentry();
const useCases = initUseCases();

const messagingClient = initMessaging();

// Listen for auth changes from other iframes (e.g., email agent)
onInvalidateUserQuery(() => {
  queryClient
    .invalidateQueries({ queryKey: userQueryKey.userInfo() })
    .then(() =>
      queryClient.fetchQuery<User>({
        queryKey: userQueryKey.userInfo(),
        queryFn: () => userApi.getUser(),
      })
    )
    .catch(console.error);
});

const messageHandler = makeMessageHandler({
  postConfig,
  postBadgeVisibilityConfig,
  setCompanyPage: appStore.setCompanyPage,
  setContactPage: appStore.setContactPage,
  setSearchPage: appStore.setSearchPage,
  setCompanyDomain: appStore.setCompanyDomain,
  setCollectedContactInfo: appStore.setCollectedContactInfo,
  setCollectedContactsSearchInfo: appStore.setCollectedContactsSearchInfo,
  setHomePage: appStore.setHomePage,
  setIsOpen: appStore.setIsOpen,
  setCompanyLinkedinId: appStore.setCompanyLinkedinId,
  setCompanyTabName: appStore.setCompanyTabName,
  setUrl: appStore.setUrl,
  setSettingsStorageValue: appStore.setSettingsStorageValue,
  getSettingsStorageValue: appStore.getSettingsStorageValue,
  setSearchPagePagination: appStore.setSearchPagePagination,
  setCalendarPage: appStore.setCalendarPage,
  setCollectedMeeting: appStore.setCollectedMeeting,
  resetCollectedMeeting: appStore.resetCollectedMeeting,
  getCollectedMeeting: appStore.getCollectedMeeting,
  getState: appStore.getState,
  setSettingsPage: appStore.setSettingsPage,
  setCollectedEmail: appStore.setCollectedEmail,
  setEmailPage: appStore.setEmailPage,
  setGoogleSheetsPage: appStore.setGoogleSheetsPage,
  setCollectedDomains: appStore.setCollectedDomains,
  setWidgetId: appStore.setWidgetId,
});

messagingClient.registerHandler(messageHandler);

if (initialState.mode === AppMode.Extension) {
  postCss();
  postChromeTabUpdateConfig();
}

const root = document.getElementById('root');
if (!root) {
  throw new Error('No root element found');
}

ReactDOM.createRoot(root).render(<App queryClient={queryClient} appStore={appStore} useCases={useCases} gb={gb} />);
